import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import styles from "styles.module.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import {
	getUserAgreementsApi,
	approveUserAgreementUserApi,
	deleteUserAgreementUserApi,
} from "api/userAgreement";
import { ICON_NAMES } from "utils/constant";
import { t } from "i18next";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import CustomButton from "ui/Button";
import { agreementUsersGridColumns } from "./columnGrid";
import { useNavigate } from "react-router";
import { PageTypePath } from "utils/pages";
import { setError, useInfoStore } from "store/error"; // store-based error handling
import { ErrorModal } from "ui/ErrorInfo"; // component that displays modal
import { CustomError } from "interface/index";
import { AgreementUser } from "interface/userAgreement";
import "./style.css";

interface UserAgreementsMobileListProps {
	items: AgreementUser[];
	loading: boolean;
	onApprove: (item: AgreementUser) => void;
	onDelete: (item: AgreementUser) => void;
}

type AgreementUserItem = AgreementUser & Record<string, any>;

const getValue = (item: AgreementUserItem, keys: string[]) => {
	for (const key of keys) {
		const value = item[key];
		if (value !== undefined && value !== null && `${value}`.trim() !== "") {
			return `${value}`;
		}
	}

	return "";
};

const normalizeMultiValue = (value?: string | null) => {
	if (!value) {
		return "-";
	}

	return (
		value
			.split("<br/>")
			.map((item) => item.trim())
			.filter(Boolean)
			.join(", ") || "-"
	);
};

const getFullName = (item: AgreementUserItem) => {
	const firstName = getValue(item, ["userFirstName", "firstName"]);
	const lastName = getValue(item, ["userLastName", "lastName"]);
	const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

	if (fullName) {
		return fullName;
	}

	return getValue(item, ["userEmail", "email"]) || "-";
};

const getInitials = (item: AgreementUserItem) => {
	const firstName = getValue(item, ["userFirstName", "firstName"]);
	const lastName = getValue(item, ["userLastName", "lastName"]);
	const parts = [firstName, lastName].filter(Boolean);

	if (parts.length === 0) {
		return "-";
	}

	return parts.map((part) => part[0]).join("").toUpperCase();
};

const UserAgreementsMobileList: FC<UserAgreementsMobileListProps> = ({
	items,
	loading,
	onApprove,
	onDelete,
}) => {
	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	return (
		<div className="user-agreement-list-mobile">
			{empty && (
				<div className="user-agreement-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, itemIndex) => {
				const agreementItem = item as AgreementUserItem;
				const itemKey = `${item.id || "noagreement"}-${itemIndex}`;
				const email = getValue(agreementItem, ["userEmail", "email"]) || "-";
				const phone = getValue(agreementItem, ["userPhoneDisplay", "phone"]) || "-";
				const buyer = normalizeMultiValue(
					getValue(agreementItem, ["buyerDisplay", "buyerName"]),
				);
				const vessel = normalizeMultiValue(
					getValue(agreementItem, ["vesselDisplay", "vesselName", "boatName"]),
				);
				const terminal = normalizeMultiValue(
					getValue(agreementItem, ["terminalDisplay", "terminalName"]),
				);

				return (
					<div key={itemKey} className="user-agreement-list-card">
						<div className="user-agreement-list-card-header">
							<div className="user-agreement-list-card-initials">
								{getInitials(agreementItem)}
							</div>
							<div className="user-agreement-list-card-name">
								{getFullName(agreementItem)}
							</div>
							<div className="user-agreement-list-card-email">{email}</div>
						</div>

						<div className="user-agreement-list-card-body">
							<div>
								<span>{t("PhoneNumber")}</span>
								<strong>{phone}</strong>
							</div>
							<div>
								<span>{t("Buyer")}</span>
								<strong>{buyer}</strong>
							</div>
							<div>
								<span>{t("Vessel")}</span>
								<strong>{vessel}</strong>
							</div>
							<div>
								<span>{t("Terminal")}</span>
								<strong>{terminal}</strong>
							</div>
						</div>

						<div className="user-agreement-list-card-actions">
							<button
								type="button"
								className="user-agreement-list-icon-btn"
								onClick={() => onApprove(item)}
								title={t("Approve")}
							>
								<img
									src={`/icon/${ICON_NAMES.GreenCheckbox}.svg`}
									alt={t("Approve")}
								/>
							</button>
							<button
								type="button"
								className="user-agreement-list-icon-btn"
								onClick={() => onDelete(item)}
								title={t("Delete")}
							>
								<img src={`/icon/${ICON_NAMES.Delete}.svg`} alt={t("Delete")} />
							</button>
						</div>
					</div>
				);
			})}

			{loading && (
				<div className="user-agreement-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default function UserAgreementsList() {
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const [forceUpdate, setForceUpdate] = useState(false);
	const [isMobileView, setIsMobileView] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}

		return window.innerWidth < 768;
	});
	const [mobileData, setMobileData] = useState<AgreementUser[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const navigate = useNavigate();
	useInfoStore();

	const loadMobileData = async () => {
		setMobileLoading(true);

		try {
			const response = await getUserAgreementsApi();
			setMobileData((response || []) as AgreementUser[]);
		} catch (e: any) {
			setMobileData([]);
			setError(
				new CustomError(t("ErrorDescription"), t("Failed", "Failed")),
			);
		} finally {
			setMobileLoading(false);
		}
	};

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		loadMobileData();
	}, [isMobileView, forceUpdate]);

	const handleApprove = async (id: number) => {
		try {
			const response = await approveUserAgreementUserApi(id);
			navigate(`/${PageTypePath.UserInformation}/${response.data.userId}`);
			setForceUpdate((prev) => !prev);
		} catch (e: any) {
			setError(
				new CustomError(
					t("ErrorDescription"),
					`${t("Approve")} ${t("Failed")}`,
				),
			);
		}
	};

	const handleDelete = async (id: number) => {
		try {
			await deleteUserAgreementUserApi(id);
			setForceUpdate((prev) => !prev);
		} catch (e: any) {
			setError(
				new CustomError(
					t("ErrorDescription"),
					`${t("Delete")} ${t("Failed")}`,
				),
			);
		}
	};

	return (
		<div className={styles.PageContainer}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("UserAgreement")}</legend>
			</div>

			{isMobileView ? (
				<UserAgreementsMobileList
					items={mobileData}
					loading={mobileLoading}
					onApprove={(item) => handleApprove(item.id)}
					onDelete={(item) => handleDelete(item.id)}
				/>
			) : (
				<GridWithGroupPaging
					key={forceUpdate ? "force-1" : "force-0"}
					dataKey="id"
					gridColumns={agreementUsersGridColumns()}
					exportGridAsPDFRef={pdfRef}
					exportGridAsExcelRef={excelRef}
					pageable={false}
					hideVirtualScroll={true}
					apiMethod={getUserAgreementsApi}
					itemAboveHeight={50}
					lastColBtns={{
						width: 200,
						cells: {
							data: (props) => {
								return (
									<td
										style={{
											display: "flex",
											justifyContent: "flex-end",
											gap: 20,
										}}
									>
										<CustomButton
											onClick={() => handleApprove(props.dataItem.id)}
											iconName={ICON_NAMES.GreenCheckbox}
											btnStyle={{ backgroundColor: "transparent" }}
											label=""
										/>
										<CustomButton
											onClick={() => handleDelete(props.dataItem.id)}
											iconName={ICON_NAMES.Delete}
											btnStyle={{ backgroundColor: "transparent" }}
											label=""
										/>
									</td>
								);
							},
						},
					}}
				/>
			)}
			<ErrorModal />
		</div>
	);
}
